import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

// ── HELPERS ──────────────────────────────────────────────────────────────────
function fmtMoney(n) {
  return `$${(n || 0).toFixed(2)}`;
}
function fmtPct(n) {
  return `${Math.round(n || 0)}%`;
}

// ── METRIC CARD ───────────────────────────────────────────────────────────────
function MetricCard({ label, value, sub, accent, starColor }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12,
      border: '1px solid var(--border)',
      padding: '1.4rem 1.5rem',
      borderLeft: accent ? '3px solid var(--orange)' : '1px solid var(--border)',
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
        {label}
      </div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.9rem', fontWeight: 900, color: starColor || (accent ? 'var(--orange)' : 'var(--ink)'), lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.4rem' }}>{sub}</div>}
    </div>
  );
}

// ── SHARE RESTAURANT CARD ─────────────────────────────────────────────────────
// The one shareable link for this restaurant, plus a scannable QR code
// for table tents, signage, or social posts. Uses the /r/slug canonical
// URL form specifically (not ?restaurant=slug) -- confirmed this
// project as the form that correctly serves restaurant-specific
// branding directly in the raw HTML response on first load, which is
// what iOS "Add to Home Screen" actually reads from (see
// TableChatMVP.jsx's manifest-injection effect for the full context).
function ShareRestaurantCard({ restaurant }) {
  const [copied, setCopied] = React.useState(false);

  if (!restaurant?.slug) return null;

  const shareUrl = `https://app.tablechat-ai.com/?restaurant=${restaurant.slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(shareUrl)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err.message);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(qrUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${restaurant.slug}-qr-code.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.warn('Direct download failed, opening image instead:', err.message);
      window.open(qrUrl, '_blank');
    }
  };

  return (
    <div style={{
      background: 'white', borderRadius: 12, border: '1px solid var(--border)',
      padding: '1.4rem 1.5rem', marginBottom: '2rem',
      display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
    }}>
      <img
        src={qrUrl}
        alt={`QR code for ${restaurant.name}`}
        style={{ width: 110, height: 110, borderRadius: 8, border: '1px solid var(--border)', flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
          Your restaurant's link
        </div>
        <div style={{
          fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--ink)',
          background: 'var(--warm)', borderRadius: 6, padding: '0.5rem 0.7rem',
          marginBottom: '0.8rem', wordBreak: 'break-all',
        }}>
          {shareUrl}
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.8rem' }}>
          Share this link or QR code anywhere -- your website, social media, table tents, or signage -- so diners can order directly from you.
        </p>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <button
            onClick={handleCopy}
            style={{ background: copied ? '#16A34A' : 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          <button
            onClick={handleDownload}
            style={{ background: 'none', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
          >
            Download QR code
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SECTION HEADER ────────────────────────────────────────────────────────────
function SectionHeader({ title, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
      <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.1rem', fontWeight: 700 }}>{title}</h2>
      {count !== undefined && (
        <span style={{
          background: 'var(--orange)', color: 'white',
          fontSize: '0.72rem', fontWeight: 700,
          padding: '0.15rem 0.55rem', borderRadius: 20,
        }}>{count}</span>
      )}
    </div>
  );
}

// ── EMPTY STATE ───────────────────────────────────────────────────────────────
function Empty({ icon, message }) {
  return (
    <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--muted)' }}>
      <div style={{ fontSize: '1.8rem', marginBottom: '0.6rem', opacity: 0.5 }}>{icon}</div>
      <p style={{ fontSize: '0.875rem' }}>{message}</p>
    </div>
  );
}

// ── PERIOD SELECTOR ───────────────────────────────────────────────────────────
function PeriodSelector({ value, onChange }) {
  const options = [
    { label: '30 days', value: 30 },
    { label: '60 days', value: 60 },
    { label: 'All time', value: 'all' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, background: 'var(--warm)', borderRadius: 8, padding: 3 }}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: '0.3rem 0.75rem',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.78rem',
            fontWeight: 600,
            background: value === opt.value ? 'white' : 'transparent',
            color: value === opt.value ? 'var(--orange)' : 'var(--muted)',
            boxShadow: value === opt.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { restaurant } = useAuth();
  const [period, setPeriod]             = useState(30);
  const [metrics, setMetrics]           = useState(null);
  const [topDishes, setTopDishes]       = useState([]);
  const [reviewStats, setReviewStats]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [lastUpdated, setLastUpdated]   = useState(null);

  // Load period-based metrics
  const loadMetrics = useCallback(async () => {
    if (!restaurant?.id) return;
    setMetricsLoading(true);
    const daysParam = period === 'all' ? 'days=3650' : `days=${period}`;
    const [analyticsData, reviewsData, allReservationsData] = await Promise.all([
      portalFetch(`/analytics?${daysParam}`),
      portalFetch('/reviews'),
      portalFetch('/reservations?filter=all'),
    ]);

    setReviewStats(reviewsData?.aggregate || null);

    const periodOrders = (analyticsData?.orders || []).filter(o => o.payment_status === 'paid');

    // Total + average order value
    const totalValue = periodOrders.reduce((sum, o) => sum + (o.total || 0), 0);
    const avgValue   = periodOrders.length > 0 ? totalValue / periodOrders.length : 0;

    // Amount redeemed (loyalty rewards)
    const amountRedeemed = periodOrders.reduce((sum, o) => sum + (o.loyalty_reward_amount || 0), 0);

    // Repeat customer rate
    const emailCounts = {};
    periodOrders.forEach(o => {
      const key = o.customer_email || o.customer_name;
      if (key) emailCounts[key] = (emailCounts[key] || 0) + 1;
    });
    const uniqueCustomers  = Object.keys(emailCounts).length;
    const repeatCustomers  = Object.values(emailCounts).filter(c => c > 1).length;
    const repeatRate       = uniqueCustomers > 0 ? (repeatCustomers / uniqueCustomers) * 100 : 0;

    // Reservations total — filter by period from the full reservations list
    const allReservations = allReservationsData?.reservations || [];
    const sinceDate = period === 'all' ? null : new Date(Date.now() - period * 24 * 60 * 60 * 1000);
    const totalReservations = sinceDate
      ? allReservations.filter(r => new Date(r.created_at) >= sinceDate).length
      : allReservations.length;

    // Top dishes
    const dishMap = {};
    periodOrders.forEach(order => {
      (order.order_items || []).forEach(item => {
        const key = item.dish_name || item.name;
        if (!key) return;
        if (!dishMap[key]) dishMap[key] = { name: key, qty: 0, revenue: 0 };
        dishMap[key].qty     += (item.quantity || 1);
        dishMap[key].revenue += (item.dish_price || 0) * (item.quantity || 1);
      });
    });
    const sorted = Object.values(dishMap).sort((a, b) => b.qty - a.qty).slice(0, 5);
    setTopDishes(sorted);

    setMetrics({
      totalValue,
      avgValue,
      amountRedeemed,
      repeatRate,
      uniqueCustomers,
      repeatCustomers,
      totalReservations,
      orderCount: periodOrders.length,
    });

    setLastUpdated(new Date());
    setMetricsLoading(false);
    setLoading(false);
  }, [restaurant?.id, period]);

  // Initial load
  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  // Re-load metrics when period changes
  useEffect(() => {
    if (!loading) loadMetrics();
  }, [period]); // eslint-disable-line

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => { loadMetrics(); }, 60000);
    return () => clearInterval(interval);
  }, [loadMetrics]);

  const now = new Date();
  const greetingHour = now.getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 17 ? 'Good afternoon' : 'Good evening';

  // Dish rating display
  const hasRating  = reviewStats?.avgRating && reviewStats?.total >= 3;
  const ratingValue = hasRating ? `★${reviewStats.avgRating}` : '–';
  const ratingSub   = hasRating
    ? `from ${reviewStats.total} dish review${reviewStats.total !== 1 ? 's' : ''}`
    : reviewStats?.total > 0
    ? `${reviewStats.total} review${reviewStats.total !== 1 ? 's' : ''} so far`
    : 'No reviews yet';

  const periodLabel = period === 'all' ? 'all time' : `last ${period} days`;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 32, height: 32, border: '2px solid var(--border)', borderTopColor: 'var(--orange)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 0.8rem' }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>{greeting}</p>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>
          {restaurant?.name}
        </h1>
        <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          {lastUpdated && ` · Updated ${lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
        </p>
      </div>

      {/* Metrics header row with period selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', fontWeight: 700, color: 'var(--ink)' }}>
          At a glance
          {metricsLoading && (
            <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--muted)', fontFamily: 'inherit', fontWeight: 400 }}>
              updating...
            </span>
          )}
        </h2>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Metric cards — 3 top row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
        <MetricCard
          label="Total order value"
          value={fmtMoney(metrics?.totalValue)}
          sub={`${metrics?.orderCount || 0} paid orders · ${periodLabel}`}
          accent
        />
        <MetricCard
          label="Average order value"
          value={fmtMoney(metrics?.avgValue)}
          sub={`Per paid order · ${periodLabel}`}
        />
        <MetricCard
          label="Amount redeemed"
          value={fmtMoney(metrics?.amountRedeemed)}
          sub={`Loyalty rewards used · ${periodLabel}`}
        />
      </div>

      {/* Metric cards — 3 bottom row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        <MetricCard
          label="Total reservations"
          value={metrics?.totalReservations ?? '–'}
          sub={`Bookings received · ${periodLabel}`}
        />
        <MetricCard
          label="Repeat customer rate"
          value={fmtPct(metrics?.repeatRate)}
          sub={metrics?.uniqueCustomers > 0
            ? `${metrics.repeatCustomers} of ${metrics.uniqueCustomers} customers returned`
            : `No customer data yet`}
        />
        <MetricCard
          label="Dish rating"
          value={ratingValue}
          sub={ratingSub}
          starColor={hasRating ? '#F59E0B' : undefined}
        />
      </div>

      <ShareRestaurantCard restaurant={restaurant} />

      {/* Top dishes */}
      <div style={styles.section}>
        <SectionHeader title={`Top dishes — ${periodLabel}`} />
        {topDishes.length === 0
          ? <Empty icon="🍜" message="Order data will appear here once customers start ordering." />
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.8rem' }}>
              {topDishes.map((dish, i) => (
                <div key={dish.name} style={{
                  background: i === 0 ? 'rgba(232,93,4,0.06)' : 'var(--warm)',
                  border: i === 0 ? '1px solid rgba(232,93,4,0.2)' : '1px solid var(--border)',
                  borderRadius: 10, padding: '1rem',
                }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: i === 0 ? 'var(--orange)' : 'var(--muted)', marginBottom: '0.4rem' }}>
                    #{i + 1} seller
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--ink)', marginBottom: '0.5rem', lineHeight: 1.3 }}>
                    {dish.name}
                  </div>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.3rem', fontWeight: 900, color: 'var(--orange)' }}>
                    {dish.qty}×
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 2 }}>
                    {fmtMoney(dish.revenue)} revenue
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </div>
    </div>
  );
}

const styles = {
  section: {
    background: 'white',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '1.4rem',
    marginBottom: '1.5rem',
  },
};
